export type ProjectStatus = "initiated" | "in-progress" | "completed"

export interface PortfolioProject {
  slug: string
  title: string
  medium: string
  description: string
  screenshot: string | null
  timeline: string
  status: ProjectStatus
  timestamp: string
  servicesOpted: string[]
  statusInitiated: string
  progress: number
  completed: string
  notesUpdates: string[]
  whatsBuilt: string
}

export const portfolioProjects: PortfolioProject[] = [
  {
    slug: "nmap",
    title: "Nmap",
    medium: "Network Discovery & Reconnaissance",
    description: "Industry-standard network mapping and port scanning tool for discovering hosts, services, and operating systems across your attack surface. Essential for reconnaissance in every engagement.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Network Mapping", "Port Scanning", "OS Fingerprinting", "Service Detection"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["NSE scripting for automated vulnerability detection", "Service/version detection on all ports", "Firewall evasion techniques"],
    whatsBuilt: "Automated network reconnaissance workflows using Nmap's NSE engine for service discovery, OS fingerprinting, and vulnerability enumeration across enterprise-scale networks.",
  },
  {
    slug: "wireshark",
    title: "Wireshark",
    medium: "Network Analysis & Forensics",
    description: "Deep packet inspection and network traffic analysis for threat hunting, incident response, and protocol forensics. Capture and analyze live traffic or saved packet captures.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Packet Analysis", "Traffic Forensics", "Protocol Dissection", "Threat Hunting"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Malware traffic pattern analysis", "Custom display filters for IOC hunting", "PCAP correlation with SIEM alerts"],
    whatsBuilt: "Enterprise traffic analysis pipelines leveraging Wireshark for deep packet inspection, malware traffic identification, and post-incident forensic reconstruction.",
  },
  {
    slug: "burp-suite",
    title: "Burp Suite",
    medium: "Web Application Security Testing",
    description: "Advanced web application security testing platform for identifying OWASP Top 10 vulnerabilities. Intercept, inspect, and modify traffic between browser and target applications.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Web App Pentesting", "API Security Testing", "Session Analysis", "Auth Bypass Testing"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Custom extensions for automated scanning", "Intruder-based brute force & fuzzing", "Repeater for manual exploit verification"],
    whatsBuilt: "Comprehensive web application security assessment frameworks using Burp Suite Pro for automated scanning, manual exploitation, and custom extension development.",
  },
  {
    slug: "metasploit",
    title: "Metasploit",
    medium: "Penetration Testing Framework",
    description: "The world's most used penetration testing framework for developing and executing exploit code against remote targets. Validate vulnerabilities with safe, controlled exploitation.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Exploit Development", "Vulnerability Validation", "Post-Exploitation", "Payload Generation"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Custom module development for zero-day research", "Automated post-exploitation with Meterpreter", "Evasion techniques against modern EDR"],
    whatsBuilt: "Enterprise penetration testing workflows using Metasploit for controlled exploitation, payload generation, and post-exploitation assessment in isolated lab environments.",
  },
  {
    slug: "owasp-zap",
    title: "OWASP ZAP",
    medium: "Automated Security Scanner",
    description: "Open-source web application security scanner for automated vulnerability detection in CI/CD pipelines. Ideal for DevSecOps integration and continuous security testing.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["DAST Scanning", "CI/CD Integration", "API Security Testing", "Passive Scanning"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Pipeline integration with GitHub Actions & Jenkins", "API scanning with OpenAPI/Swagger support", "Automated baseline and full scans"],
    whatsBuilt: "Integrated DAST pipelines using OWASP ZAP for automated security scanning within CI/CD workflows, catching vulnerabilities before production deployment.",
  },
  {
    slug: "kali-linux",
    title: "Kali Linux",
    medium: "Security Distribution & Toolchain",
    description: "Enterprise-grade penetration testing distribution with 600+ pre-installed security tools. The standard platform for security assessments, digital forensics, and reverse engineering.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Penetration Testing", "Digital Forensics", "Reverse Engineering", "Red Team Operations"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Custom ISO builds for client engagements", "Persistent encrypted storage for field operations", "Armitage for team-based collaboration"],
    whatsBuilt: "Tailored Kali Linux environments with custom toolchains and persistent encrypted storage for red team operations, forensic analysis, and client engagements.",
  },
  {
    slug: "nessus",
    title: "Nessus",
    medium: "Vulnerability Assessment Platform",
    description: "Industry-leading vulnerability scanner for identifying CVEs, misconfigurations, and compliance gaps across your entire infrastructure. Agent-based and remote scanning capabilities.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Vulnerability Scanning", "Compliance Auditing", "Patch Gap Analysis", "Risk Prioritization"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Agent-based scanning for air-gapped environments", "Custom compliance policies (PCI-DSS, HIPAA, SOC2)", "Remediation tracking and reporting"],
    whatsBuilt: "Enterprise vulnerability management programs using Nessus for continuous scanning, compliance auditing, and risk-based prioritization across hybrid infrastructure.",
  },
  {
    slug: "snort",
    title: "Snort",
    medium: "Intrusion Detection & Prevention",
    description: "Real-time network intrusion detection and prevention system for analyzing traffic patterns and blocking malicious activity. Rule-based detection with community threat intelligence.",
    screenshot: null,
    timeline: "Active Use",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["IDS/IPS Deployment", "Rule Development", "Traffic Analysis", "Threat Intelligence"],
    statusInitiated: "2024-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Custom rule sets for client-specific threats", "PulledPork for automated rule management", "Barnyard2 for database output integration"],
    whatsBuilt: "Enterprise IDS/IPS deployments using Snort with custom rule sets, automated signature updates, and SIEM integration for real-time threat detection and prevention.",
  },
  {
    slug: "tucas",
    title: "Tucas",
    medium: "Next-gen DFX Experience",
    description: "Swap it anywhere, anytime. Premium multi-chain swapping with institutional-grade routing. Non-custodial, secure, and designed for speed.",
    screenshot: "/image copy 8.png",
    timeline: "2025 – Present",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["DeFi Swap", "Multi-chain Routing", "Advanced Trading"],
    statusInitiated: "2025-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["12+ networks", "Optimized routes", "0.1% fee"],
    whatsBuilt: "DeFi swap platform with Launch Swap and Advanced Trading. Swap widget (ETH/MATIC), Route/Price Impact/Fee display. Cross-chain, non-custodial, best execution, pro insights. Connect wallet.",
  },
  {
    slug: "mindcare",
    title: "MindCare",
    medium: "Hospital Mental Health Platform",
    description: "Your Mental Health Journey Starts Here. Connect instantly with verified psychology counsellors via chat, voice, or video calls. Get professional support when you need it most.",
    screenshot: "/image copy 9.png",
    timeline: "2025 – Present",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Mental Health Dashboard", "Counsellor Matching", "Video Consultations"],
    statusInitiated: "2025-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["500+ Verified Counsellors", "10K+ Happy Clients", "50K+ Sessions", "4.8/5 Rating"],
    whatsBuilt: "Hospital mental health dashboard with hero section, Get Started Free and Browse Counsellors. Start Your First Session card. Stats: verified counsellors, clients, sessions, rating. Blue/purple gradient design.",
  },
  {
    slug: "cyrosolution",
    title: "Cyro Solutions",
    medium: "AI Receptionist Platform",
    description: "Never miss another customer call. Cyro Solutions gives your business a 24/7 AI receptionist that answers calls, responds to texts, and books appointments directly into your calendar—so you stop losing money every time you can't pick up the phone.",
    screenshot: "/image copy 10.png",
    timeline: "2025 – Present",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["AI Receptionist", "Voice + SMS", "Calendar Integration"],
    statusInitiated: "2025-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["1,247 calls answered/week", "342 appointments", "-89% missed calls"],
    whatsBuilt: "Dark-themed AI receptionist landing page. Hero with Book a demo, Watch demo. Stats: Calls Answered, Appointments, Missed Calls Re. Before vs After (23 missed→47 booked). Robot figure, blue grid background.",
  },
  {
    slug: "monaddex",
    title: "MonadDEX",
    medium: "Decentralized Exchange Frontend",
    description: "Swap anytime, anywhere. The largest onchain marketplace. Buy and sell crypto on Monad and 12+ other chains.",
    screenshot: "/image copy 11.png",
    timeline: "2025 – Present",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["DEX UI", "Token Swap", "Multi-chain Support"],
    statusInitiated: "2025-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Sell/Buy swap widget", "MONAD token", "Advanced Trading"],
    whatsBuilt: "DEX frontend with dark purple/black theme, pink gradient accents. Swap widget (Sell MONAD / Buy token), Connect wallet, Advanced Trading. Nav: Limit Trading, Explore, Pool.",
  },
  {
    slug: "cogy",
    title: "Cogy",
    medium: "App Builder by Cogerphere",
    description: "Build your app with Cogy. Describe your app idea and watch Cogerphere build it for you in minutes, not months.",
    screenshot: "/image copy 12.png",
    timeline: "2025 – Present",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["App Builder UI", "Natural Language Input", "Ideas Suggestions"],
    statusInitiated: "2025-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["Beta 0.1", "Ideas dropdown", "Build button"],
    whatsBuilt: "Glassmorphism app builder with vibrant gradient background (purple-blue to yellow-orange). Prompt input for app ideas, Ideas dropdown, Build CTA. Features, Pricing, Docs, Support. Sign in / Sign up.",
  },
  {
    slug: "link",
    title: "Link",
    medium: "Landing Page & Business Platform",
    description: "Modern dark-themed landing page with lime green accents. Professional design featuring hero section, email signup, rating display, and floating metric cards.",
    screenshot: "/image copy 2.png",
    timeline: "2025 – Present",
    status: "completed",
    timestamp: "2026-03-04",
    servicesOpted: ["Landing Page", "Brand Design", "Conversion Optimization"],
    statusInitiated: "2025-01-01",
    progress: 100,
    completed: "2026-03-04",
    notesUpdates: ["4.9 rating display", "Email signup CTA", "Analytics metrics cards"],
    whatsBuilt: "Dark green/charcoal landing page with globe logo, navigation, hero with highlighted headline, email capture, circular profile imagery, and floating rating and metric elements.",
  },
]
